import _styled, { default as _styled2, default as _styled3, default as _styled4 } from "styled-components";
const _excluded = ["style", "css", "className", "children", "variant", "size", "color", "textColor", "disabled", "subStatus", "stacked"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import { variantSwitch } from '@adsk/alloy-react-helpers';
import theme, { stylePropType, flattenStyle } from '@adsk/alloy-react-theme';
import { BADGE_VARIANTS, BADGE_SIZES, BADGE_COLORS } from './consts';
/**
 * A simple component that renders a Badge based on a className/style passed
 */
const Badge = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      style,
      css,
      className,
      children,
      variant = BADGE_VARIANTS.SOLID,
      size = BADGE_SIZES.MEDIUM,
      color = BADGE_COLORS.DEFAULT,
      textColor = theme.tokens.colors.text.inverse.value,
      disabled,
      subStatus,
      stacked
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const showSubStatus = variant === BADGE_VARIANTS.LINE && subStatus;
  return /*#__PURE__*/React.createElement(_StyledSpan, _extends({
    className: className,
    ref: ref,
    style: flattenStyle(style)
  }, props, {
    $_css: [_objectSpread({
      padding: `${theme.spacing.XXXS}px ${theme.spacing.XS}px`,
      borderRadius: theme.borderRadius.pill,
      display: 'inline-flex'
    }, disabled && {
      color: theme.tokens.colors.text.inactive.value
    }), variantSwitch({
      statement: variant,
      variants: {
        [BADGE_VARIANTS.SOLID]: _objectSpread({
          color: textColor,
          backgroundColor: color
        }, disabled && {
          color: theme.tokens.colors.text.inactive.value,
          backgroundColor: theme.tokens.colors.background.neutral.inactive.value
        }),
        [BADGE_VARIANTS.OUTLINE]: {
          border: `1px solid ${theme.tokens.colors.border.value}`
        },
        [BADGE_VARIANTS.LINE]: {
          display: 'flex',
          flexWrap: 'wrap'
        }
      }
    }), variantSwitch({
      statement: size,
      variants: {
        [BADGE_SIZES.SMALL]: _objectSpread({}, theme.typography.labelSmall),
        [BADGE_SIZES.MEDIUM]: _objectSpread({}, theme.typography.labelMedium)
      }
    }), css]
  }), variant === BADGE_VARIANTS.LINE && /*#__PURE__*/React.createElement(_StyledSpan2, {
    $_css2: [_objectSpread({
      width: 4,
      transform: 'scaleX(0.75)',
      borderRadius: 4,
      marginRight: theme.spacing.XS,
      backgroundColor: color
    }, disabled && {
      backgroundColor: theme.tokens.colors.background.neutral.inactive.value
    })]
  }), /*#__PURE__*/React.createElement(_StyledSpan3, {
    $_css3: [{
      display: 'flex',
      flexDirection: stacked ? 'column' : 'row'
    }]
  }, children, showSubStatus && /*#__PURE__*/React.createElement(_StyledSpan4, {
    $_css4: [{
      color: disabled ? theme.tokens.colors.text.inactive.value : theme.tokens.colors.text.secondary.value
    }, variantSwitch({
      statement: stacked === null || stacked === void 0 ? void 0 : stacked.toString(),
      variants: {
        false: _objectSpread(_objectSpread({}, theme.typography.bodyMedium), {}, {
          paddingLeft: theme.spacing.XS
        }),
        true: theme.typography.bodySmall
      }
    }), variantSwitch({
      statement: size,
      variants: {
        [BADGE_SIZES.SMALL]: _objectSpread({}, theme.typography.bodySmall)
      }
    })]
  }, subStatus)));
});
Badge.displayName = 'Badge';
Badge.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Badge style variant, defaults to 'solid' */
  variant: PropTypes.oneOf(Object.values(BADGE_VARIANTS)),
  /** Badge size, defaults to 'medium' */
  size: PropTypes.oneOf(Object.values(BADGE_SIZES)),
  /** Sub status to display, only in `line` variant */
  subStatus: PropTypes.any,
  /** When using subStatus and set to true, the sub status is under the label */
  stacked: PropTypes.bool,
  /** Color to apply on the background of the Badge */
  color: PropTypes.string,
  /** Color to apply on the text */
  textColor: PropTypes.string,
  /** Sets the badge in disabled state */
  disabled: PropTypes.bool,
  /** The component's children, here you should put the text */
  children: PropTypes.any
};
export default Object.assign(Badge, {
  VARIANTS: BADGE_VARIANTS,
  COLORS: BADGE_COLORS,
  SIZES: BADGE_SIZES
});
var _StyledSpan = _styled("span")`${p => p.$_css}`;
var _StyledSpan2 = _styled2("span")`${p => p.$_css2}`;
var _StyledSpan3 = _styled3("span")`${p => p.$_css3}`;
var _StyledSpan4 = _styled4("span").withConfig({
  displayName: "Badge___StyledSpan4",
  componentId: "sc-xpbx4o-0"
})(["", ""], p => p.$_css4);
//# sourceMappingURL=Badge.js.map